// useLocale.tsx — hook + provider pour la gestion de la langue de l'app.
//
// Logique :
//   - Au mount, si AUCUN choix n'est en localStorage, on appelle
//     /api/i18n/detect-locale (basé sur l'IP via geoip-lite côté backend)
//     pour décider de la langue. Sinon, on garde le choix sauvé.
//   - changeLocale(loc) : applique immédiatement i18next + sauve en
//     localStorage. Le user voit son choix appliqué partout instantanément
//     (re-render du tree React grâce à react-i18next).

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import i18n, { DEFAULT_LOCALE, STORAGE_KEY, SUPPORTED_LOCALES, type Locale } from "../i18n";

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  detectedFromIp: boolean;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function isLocale(s: string | null | undefined): s is Locale {
  return !!s && (SUPPORTED_LOCALES as readonly string[]).includes(s);
}

export function LocaleProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(
    isLocale(i18n.language) ? (i18n.language as Locale) : DEFAULT_LOCALE
  );
  const [detectedFromIp, setDetectedFromIp] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function detectIfNoChoice() {
      let saved: string | null = null;
      try {
        saved = window.localStorage.getItem(STORAGE_KEY);
      } catch {
        /* no-op */
      }
      if (isLocale(saved)) return; // l'user a déjà choisi, on respecte

      // Pas de choix : on demande au backend de détecter selon l'IP.
      try {
        const res = await fetch("/api/i18n/detect-locale");
        const data = await res.json();
        if (cancelled) return;
        if (data?.success && isLocale(data.locale) && data.locale !== i18n.language) {
          await i18n.changeLanguage(data.locale);
          setLocaleState(data.locale);
          setDetectedFromIp(true);
        }
      } catch {
        // Network down ou backend ko : on reste sur la locale bootstrappée
        // (Accept-Language ou DEFAULT_LOCALE). Pas critique.
      }
    }
    void detectIfNoChoice();
    return () => {
      cancelled = true;
    };
  }, []);

  const setLocale = useCallback((next: Locale) => {
    void i18n.changeLanguage(next);
    setLocaleState(next);
    setDetectedFromIp(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* no-op (Safari privé etc.) */
    }
  }, []);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, detectedFromIp }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale doit être utilisé dans <LocaleProvider>");
  return ctx;
}

// Re-export pratique pour les components qui veulent juste t().
export { useTranslation };
